import { useRef, useState } from "react";
import { buildIranWarDays } from "./iranWarData";
import "./iranWar.css";

const WHEEL_STEP = 100;
const WHEEL_RESET_DELAY = 160;
const SWIPE_THRESHOLD = 40;
const DAYS = buildIranWarDays();

const DATE_FORMATTER = new Intl.DateTimeFormat("en-US", {
  dateStyle: "full",
  timeZone: "UTC",
});

function formatDate(date) {
  return DATE_FORMATTER.format(new Date(`${date}T12:00:00Z`));
}

function IranWarPage() {
  const [dayIndex, setDayIndex] = useState(0);
  const [hasNavigated, setHasNavigated] = useState(false);
  const pageRef = useRef(null);
  const touchStartYRef = useRef(null);
  const wheelDeltaRef = useRef(0);
  const wheelResetRef = useRef(null);
  const day = DAYS[dayIndex];
  const isWar = day.status === "war";

  function changeDay(offset) {
    if (pageRef.current) pageRef.current.scrollTop = 0;
    setHasNavigated(true);
    setDayIndex((current) =>
      Math.max(0, Math.min(DAYS.length - 1, current + offset))
    );
  }

  function handleWheel(event) {
    // When a short viewport needs scrolling, keep gestures for reading the day.
    if (event.currentTarget.scrollHeight > event.currentTarget.clientHeight + 1) return;
    window.clearTimeout(wheelResetRef.current);
    let deltaY = event.deltaY;

    if (event.deltaMode === 1) {
      deltaY *= 40;
    } else if (event.deltaMode === 2) {
      deltaY *= window.innerHeight;
    }

    wheelDeltaRef.current += deltaY;
    const dayOffset = Math.trunc(wheelDeltaRef.current / WHEEL_STEP);

    if (dayOffset !== 0) {
      changeDay(dayOffset);
      wheelDeltaRef.current -= dayOffset * WHEEL_STEP;
    }

    wheelResetRef.current = window.setTimeout(() => {
      wheelDeltaRef.current = 0;
    }, WHEEL_RESET_DELAY);
  }

  function handleTouchStart(event) {
    if (event.touches.length !== 1 || event.target.closest("a, button") ||
        event.currentTarget.scrollHeight > event.currentTarget.clientHeight + 1) {
      touchStartYRef.current = null;
      return;
    }
    touchStartYRef.current = event.touches[0]?.clientY ?? null;
  }

  function handleTouchEnd(event) {
    const endY = event.changedTouches[0]?.clientY;

    if (touchStartYRef.current === null || endY === undefined) {
      return;
    }

    const distance = touchStartYRef.current - endY;
    touchStartYRef.current = null;

    if (Math.abs(distance) >= SWIPE_THRESHOLD) {
      changeDay(distance > 0 ? 1 : -1);
    }
  }

  function handleKeyDown(event) {
    if (event.currentTarget.scrollHeight > event.currentTarget.clientHeight + 1) return;
    if (event.key === "ArrowDown" || event.key === "PageDown") {
      event.preventDefault();
      changeDay(1);
    } else if (event.key === "ArrowUp" || event.key === "PageUp") {
      event.preventDefault();
      changeDay(-1);
    }
  }

  return (
    <main
      className="iranWarPage"
      ref={pageRef}
      onKeyDown={handleKeyDown}
      onTouchEnd={handleTouchEnd}
      onTouchStart={handleTouchStart}
      onTouchCancel={() => { touchStartYRef.current = null; }}
      onWheel={handleWheel}
      tabIndex="0"
    >
      <section
        aria-label={`${formatDate(day.date)}: ${isWar ? "war" : "peace"}`}
        aria-live="polite"
        className={`iranWarDay ${isWar ? "isWar" : "isPeace"}`}
      >
        <article className="iranWarCard">
          <div className="iranWarDateRow">
            <time dateTime={day.date}>{formatDate(day.date)}</time>
          </div>

          <div className="iranWarVerdict">
            <h1>{isWar ? "WAR." : "PEACE."}</h1>
          </div>

          <footer className="iranWarSource">
            <div>
              <div className="iranWarEventText">{day.headline.title}</div>
            </div>
            <div className="iranWarLinks">
              <a
                className="iranWarStatusSource"
                href={day.sourceUrl}
                target="_blank"
                rel="noreferrer"
              >
                Status source: {day.source}{" "}
                <span aria-hidden="true">↗</span>
              </a>
            </div>
          </footer>
        </article>
        <nav className="iranWarDayNav" aria-label="Browse days">
          <button type="button" onClick={() => changeDay(-1)} disabled={dayIndex === 0}>← Newer day</button>
          <button type="button" onClick={() => changeDay(1)} disabled={dayIndex === DAYS.length - 1}>Older day →</button>
        </nav>
      </section>

      {!hasNavigated && (
        <p className="iranWarScrollHint">
          <span aria-hidden="true">↓</span> Scroll for previous days
        </p>
      )}
    </main>
  );
}

export default IranWarPage;
